import {
  CustomTable,
  CustomModal,
  CustomTabs,
  useStudentReportPDF,
} from "components";
import { FilterableTable } from "components/FilterableTable";
import { useState } from "react";
import {
  studentLessonRecordTableColumns,
  studentLessonRecordTableActions,
} from "./constants";
import { useReducer, useMemo } from "react";
import { Button, Row, Col } from "reactstrap";
import { LessonDetail } from "./components/LessonDetail";
import { StudentReport } from "./components/StudentReport";
import { InClassPhotos } from "./components/InClassPhotos";
import { Classwork } from "./components/Classwork";
import { modalAction, modalReducer, modalInitialState } from "./reducers";
import apiService from "services/api";
import { useQuery } from "react-query";

const LessonRecords = ({ student }) => {
  const [lessonDetailModalProps, lessonDetailModalDispatch] = useReducer(
    modalReducer,
    modalInitialState,
  );
  const [refetchCheck, setRefetchCheck] = useState(false);

  const {
    data: subjects,
    isLoading: subjectsLoading,
    isError: subjectsError,
  } = useQuery(["courses", "lesson-record-filter"], () => apiService.course.get());

  const subjectOptions = useMemo(
    () => (subjects || []).map((course) => ({
      label: course.name,
      value: course.id,
    })),
    [subjects],
  );

  const filters = useMemo(
    () => [
      {
        filterName: "date_from",
        operator: "contains",
        placeholder: "Date",
        type: "date",
        label: "Date From",
      },
      {
        filterName: "date_to",
        operator: "contains",
        placeholder: "Date",
        type: "date",
        label: "Date To",
      },
      {
        filterName: "centre",
        operator: "contains",
        placeholder: "Centre",
      },
      {
        filterName: "teacher_name",
        operator: "contains",
        placeholder: "Teacher Name",
      },
      {
        filterName: "course_id",
        type: "searchable-select",
        placeholder: "Subject",
        options: subjectOptions,
        isLoading: subjectsLoading,
        isDisabled: subjectsLoading || subjectsError,
        noOptionsMessage: () => "No subjects found",
      },
      {
        filterName: "lesson_name",
        operator: "contains",
        placeholder: "Lesson Topic",
      },
    ],
    [subjectOptions, subjectsLoading, subjectsError],
  );

  const [PDFView, previewPDF, exportStudentReportAsPdf, exportingPDF] =
    useStudentReportPDF({
      studentReport: lessonDetailModalProps?.data?.studentReport,
      student,
    });

  const showLessonDetailModal = (row) => {
    lessonDetailModalDispatch({ type: modalAction.OPEN, payload: row });
  };

  const lessonDetailTabs = useMemo(() => {
    const lessonRecord = lessonDetailModalProps?.data;

    return [
      {
        title: "Lesson Detail",
        component: () => <LessonDetail studentRecord={lessonRecord} />,
      },
      {
        title: "Student Performance",
        component: () => (
          <StudentReport studentReport={lessonRecord?.studentReport} />
        ),
      },
      {
        title: "In Class Photos",
        component: () => (
          <InClassPhotos
            lessonFiles={lessonDetailModalProps?.data?.lessonFiles}
          />
        ),
      },
      {
        title: "Classwork",
        component: () => (
          <Classwork lessonFiles={lessonDetailModalProps?.data?.lessonFiles} />
        ),
      },
    ];
  }, [lessonDetailModalProps]);

  return (
    <Row>
      <CustomModal
        compact={true}
        show={lessonDetailModalProps.show}
        hideModal={() => lessonDetailModalDispatch({ type: modalAction.CLOSE })}
        centered
        zIndex={1}
        buttons={
          lessonDetailModalProps?.data?.studentReport ? (
            <Button
              onClick={previewPDF}
              color="success"
              disabled={exportingPDF}
            >
              Preview Student Report
            </Button>
          ) : (
            ""
          )
        }
      >
        <CustomTabs tabs={lessonDetailTabs} color="primary" border={false} />
      </CustomModal>

      <PDFView />

      <Col sm="12">
        {subjectsError && (
          <p role="alert" className="text-danger">
            Unable to load subjects. Please refresh the page to try again.
          </p>
        )}
        <FilterableTable
          query={(filters) => () =>
            student?.id
              ? apiService.student.lessonRecords(student?.id, filters)
              : Promise.resolve(undefined)
          }
          enabled={!!student?.id}
          title="My Lesson Records"
          actionPrepend
          queryKey={["studentRecords", "self"]}
          columns={studentLessonRecordTableColumns(
            studentLessonRecordTableActions({
              showModal: showLessonDetailModal,
              canAdd: true,
            }),
          )}
          refetchCheck={refetchCheck}
          filters={filters}
        />
      </Col>
    </Row>
  );
};

export default LessonRecords;
